import React from 'react';
import { Building } from 'lucide-react';
import { ELQR_BANKS } from '../../data/elqrBanks';
import { KGS_BANK_APPS } from '../../data/kgsBankApps';
import { KZT_BANK_APPS } from '../../data/kztBankApps';
import { bankDisplayName, kgsBankId } from '../../lib/kgsBanks';
import { PaymentCopyButton } from './PaymentCopyFeedback';
import type { PaymentCopyField, PaymentCopyFeedback } from '../../hooks/usePaymentCopyFeedback';
import { usePaymentLanguage } from '../../lib/paymentLanguage';

interface TransferPaymentRequisitesProps {
    currency: string;
    bank?: string;
    phone?: string;
    card?: string;
    recipient?: string;
    copiedField: PaymentCopyField | null;
    feedback: PaymentCopyFeedback | null;
    onCopy: (value: string, field: PaymentCopyField) => void;
}

export const TransferPaymentRequisites = ({
    currency, bank, phone, card, recipient, copiedField, feedback, onCopy,
}: TransferPaymentRequisitesProps) => {
    const { t } = usePaymentLanguage();
    const bankName = bankDisplayName(bank);
    const bankNameParts = bankName.match(/^(.*?)\s+(\([^()]+\))$/);
    const normalize = (value: string) => value.toLowerCase().replace(/[\s!()._-]+/g, '');
    const catalog: ReadonlyArray<{ id: string; name: string; logo: string }> = currency === 'KGS' ? [...KGS_BANK_APPS, ...ELQR_BANKS]
        : currency === 'KZT' ? KZT_BANK_APPS : [];
    const logo = catalog.find(item => item.id === kgsBankId(bank)
        || [item.name, item.id].some(value => normalize(value) === normalize(bankName)))?.logo;
    const copyButton = (value: string, field: PaymentCopyField) => (
        <PaymentCopyButton className="transfer-copy" field={field} feedback={feedback} copied={copiedField === field}
            showLabel onClick={() => onCopy(value, field)} />
    );
    const displayCard = card && /^\d[\d\s]*$/.test(card)
        ? card.replace(/\s/g, '').match(/.{1,4}/g)?.join(' ') : card;

    return <div className="transfer-requisites">
        {bankName && <div className="transfer-bank" data-copy-field="bank" data-copied={copiedField === 'bank'}>
            <div className="transfer-bank-logo" aria-hidden="true">
                {logo ? <img src={logo} alt="" /> : <Building />}
            </div>
            <div className="min-w-0 flex-1">
                <p className="transfer-field-label">{t('Банк')}</p>
                <p className="transfer-bank-name">{bankNameParts ? <>
                    <span>{bankNameParts[1]}</span>{' '}<span>{bankNameParts[2]}</span>
                </> : bankName}</p>
            </div>
            {copyButton(bankName, 'bank')}
        </div>}
        {phone && <div className="transfer-field" data-copy-field="phone" data-copied={copiedField === 'phone'}>
            <p className="transfer-field-label">{t('Телефон')}</p>
            <div className="transfer-field-value">
                <p className="transfer-number">{phone}</p>
                {copyButton(phone, 'phone')}
            </div>
        </div>}
        {card && <div className="transfer-field" data-copy-field="card" data-copied={copiedField === 'card'}>
            <p className="transfer-field-label">{t('Номер карты')}</p>
            <div className="transfer-field-value">
                <p className="transfer-number transfer-card-number">{displayCard}</p>
                {copyButton(card, 'card')}
            </div>
        </div>}
        {recipient && <div className="transfer-field" data-copy-field="name" data-copied={copiedField === 'name'}>
            <p className="transfer-field-label">{t('Получатель')}</p>
            <div className="transfer-field-value">
                <p className="transfer-recipient">{recipient}</p>
                {copyButton(recipient, 'name')}
            </div>
        </div>}
    </div>;
};
